import localforage from "localforage";

const DEFAULT_OPTIONS = {
  version: 0,
  requestAfterGetCache: true,
};

// indexedDB, websql 情况下的 database 名
const NAME = "aku_store";
// indexedDB, websql 情况下的表名
const STORE_NAME = "webstore";

// 最大缓存时间
const MAX_CACHE_TIME = 7 * 24 * 60 * 60 * 1000; // 7 天
// 固定的缓存 key 前缀
const CACHE_KEY_PREFIX = "akuYFYe2W";

let cacheInstance = null;

class DataCacheService {
  constructor() {
    if (cacheInstance) {
      return cacheInstance;
    }

    this.localforage = localforage.createInstance({
      name: NAME,
      driver: [localforage.LOCALSTORAGE, localforage.INDEXEDDB, localforage.WEBSQL],
      storeName: STORE_NAME,
    });

    // 准备清除过期的数据
    this.bootstrapClear();
  }

  static getInstance() {
    if (!cacheInstance) {
      return new DataCacheService();
    }

    return cacheInstance;
  }

  getDataFromCache(options) {
    const getCacheOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    if (!this.validateOptions(getCacheOptions)) {
      throw new Error("[DataCacheService]: getDataFromCache 参数不正确");
    }

    const { cacheKey } = getCacheOptions;

    this.getCache(cacheKey)
      .then((cache) => {
        this.handleCache(cache, getCacheOptions);
      })
      .catch((err) => {
        // 获取缓存出错时，请求接口获取并回调
        this.handleFromRemote(getCacheOptions);
      });
  }

  handleCache(cache, options) {
    if (!this.validateOptions(options)) {
      return;
    }

    const { cacheKey, requestAfterGetCache, version } = options;

    if (!cache) {
      this.handleCacheNotExist(options);
    } else {
      const valid = this.isValidCache(cache, version);
      if (!valid) {
        this.removeCache(cacheKey)
          .then(() => {
            // 缓存无效时，删除缓存，然后从接口获取数据
            this.handleFromRemote(options);
          })
          .catch(() => {
            // 删除失败也获取数据
            this.handleFromRemote(options);
          });
      } else {
        // 缓存有效时，调用回调
        // 不需要再次请求时 requestAfterGetCache: false，则是最后一次回调，否则不是最后一次
        this.invokeCallback(options, null, cache.data, true, !requestAfterGetCache);

        // 需要再次从接口获取数据
        if (requestAfterGetCache) {
          this.handleFromRemote();
        }
      }
    }
  }

  bootstrapClear() {
    setTimeout(() => {
      this.clear();
    }, 3000);
  }

  validateOptions(options) {
    const { cacheKey, cacheTime, getDataFromRemote, requestAfterGetCache, callback, version } =
      options;
    if (
      cacheKey &&
      cacheTime &&
      typeof cacheTime === "number" &&
      typeof getDataFromRemote === "function" &&
      typeof callback === "function" &&
      typeof version === "number" &&
      typeof requestAfterGetCache === "boolean"
    ) {
      return true;
    }

    return false;
  }

  handleCacheNotExist(options) {
    return this.handleFromRemote(options);
  }

  /**
   * 从远端获取数据，并设置缓存
   */
  handleFromRemote(options) {
    if (!this.validateOptions(options)) {
      return;
    }

    const { getDataFromRemote } = options;
    getDataFromRemote()
      .then((data) => {
        this.setCache(data, options);
        this.invokeCallback(options, null, data, false, true);
      })
      .catch((error) => {
        this.invokeCallback(options, error, null, false, true);
      });
  }

  setCache(data, options) {
    if (!this.validateOptions(options)) {
      return;
    }

    const { version, cacheTime, cacheKey: key } = options;

    const cacheKey = this.computeKey(key);

    let time = cacheTime * 1000;
    if (time > MAX_CACHE_TIME) {
      time = MAX_CACHE_TIME;
    }

    this.localforage
      .setItem(cacheKey, {
        version: version,
        expiredTime: Date.now() + time,
        data,
      })
      .catch((error) => {
        // 设置缓存失败
        this.reportToSentry(error);
      });
  }

  getCache(key) {
    if (!key) {
      return Promise.resolve(null);
    }

    const cacheKey = this.computeKey(key);

    return this.localforage.getItem(cacheKey);
  }

  removeCache(key) {
    if (!key) {
      return;
    }

    const cacheKey = this.computeKey(key);

    return this.localforage.removeItem(cacheKey).catch((error) => {
      // 删除出错
      return Promise.reject(error);
    });
  }

  computeKey(key) {
    return `${CACHE_KEY_PREFIX}_${key}`;
  }

  invokeCallback(options, ...args) {
    const { callback } = options;

    if (typeof callback !== "function") {
      return;
    }

    try {
      callback(...args);
    } catch (error) {
      this.reportToSentry(error);
      // eslint-disable-next-line
      console.error(error);
      // eslint-disable-next-line
      console.error(
        "[DataCacheService]: 调用 callback 出现错误，可能为缓存数据与代码不兼容导致，请检查代码或设置新的缓存版本"
      );
    }
  }

  isValidCache(cache, checkVersion) {
    const { version, expiredTime, data } = cache;
    const now = Date.now();

    // 版本不匹配，缓存已过期，缓存时间大于最大缓存时间
    if (
      (typeof checkVersion !== "undefined" && version !== checkVersion) ||
      now > expiredTime ||
      expiredTime - now > MAX_CACHE_TIME
    ) {
      return false;
    }

    return true;
  }

  clear() {
    this.localforage
      .keys()
      .then((keys) => {
        keys.forEach((key) => {
          this.localforage
            .getItem(key)
            .then((value) => {
              // 删除相关的过期数据
              if (key.indexOf(CACHE_KEY_PREFIX) > -1 && value && !this.isValidCache(value)) {
                this.localforage.removeItem(key);
              }
            })
            .catch((error) => {});
        });
      })
      .catch((error) => {});
  }

  reportToSentry(error) {
    if (window.sentry) {
      window.sentry.captureException(error);
    }
  }
}

let cacheService;
if (typeof window === "undefined") {
  cacheService = {
    getDataFromCache() {},
  };
} else {
  cacheService = DataCacheService.getInstance();
}

export { cacheService };
